import { useMemo, memo } from "react";
import { storyElement } from "@/lib/story-element";
import { isHebrewText } from "@/lib/rtl-utils";
import { bidiComponents } from "@/lib/bidi-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Volume2, Download, MapPin, Clock, Dices, Brain, ScrollText, Globe, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { parseNarratorSegments, type NarratorSegment } from "@/lib/parse-narrator-segments";
import { classifySegment, CATEGORY_BADGES } from "@/lib/message-classification";
import { proseClasses } from "@/lib/prose-classes";
import { cn } from "@/lib/utils";
import type { DiceRoll } from "@/types/adventure.types";

export interface EntityMedia {
  name: string;
  image_url: string | null;
  emotional_state?: { current_mood?: string; stress_level?: number };
  type?: string;
}

interface NarratorMessageProps {
  content: string;
  entityLookup: Map<string, EntityMedia>;
  onSpeakSegment: (text: string, voiceKey: string) => void;
  onDownloadSegment?: (text: string, voiceKey: string) => void;
  speakingKey: string | null;
  className?: string;
  /** If true, hides TTS buttons and shows cursor (streaming mode) */
  streaming?: boolean;
  /** Dice rolls from message metadata — rendered inline */
  diceRolls?: DiceRoll[];
  /** Location change from metadata.state_changes.current_location */
  locationChange?: string;
  /** Scene image URL from metadata */
  sceneImageUrl?: string;
  /** Time info from state variables */
  timeInfo?: { hour?: number; day?: number; phase?: string; weather?: string };
  /** Callback when a location name is clicked */
  onLocationClick?: (locationName: string) => void;
  /** Callback when an NPC speaker name is clicked */
  onSpeakerClick?: (speakerName: string) => void;
  /** Offscreen NPC actions from metadata */
  offscreenActions?: Array<{ name: string; action: string; motivation?: string }>;
}

// proseClasses imported from @/lib/prose-classes

/** Hash a speaker name to a consistent HSL hue for left-border color */
function speakerHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

/** Map mood string to a tiny emoji */
function moodEmoji(mood?: string): string | null {
  if (!mood) return null;
  const m = mood.toLowerCase();
  if (m.includes("angry") || m.includes("furious") || m.includes("rage")) return "😠";
  if (m.includes("happy") || m.includes("joy") || m.includes("cheerful")) return "😊";
  if (m.includes("sad") || m.includes("grief") || m.includes("sorrow")) return "😢";
  if (m.includes("fear") || m.includes("afraid") || m.includes("terror")) return "😨";
  if (m.includes("suspicious") || m.includes("wary") || m.includes("distrust")) return "🤨";
  if (m.includes("calm") || m.includes("serene") || m.includes("peace")) return "😌";
  if (m.includes("amused") || m.includes("playful")) return "😏";
  if (m.includes("nervous") || m.includes("anxious")) return "😰";
  if (m.includes("curious") || m.includes("intrigued")) return "🧐";
  if (m.includes("determined") || m.includes("resolute")) return "😤";
  if (m.includes("contempt") || m.includes("disgust")) return "😒";
  return null;
}

// Memoized to avoid re-parsing markdown on every state tick (perf-audit P-29).
// Equality check: shallow by default — content/entityLookup/speakingKey identity.
export const NarratorMessage = memo(function NarratorMessage({
  content,
  entityLookup,
  onSpeakSegment,
  onDownloadSegment,
  speakingKey,
  className,
  streaming = false,
  diceRolls,
  locationChange,
  sceneImageUrl,
  timeInfo,
  onLocationClick,
  onSpeakerClick,
  offscreenActions,
}: NarratorMessageProps) {
  const segments = useMemo(() => parseNarratorSegments(content), [content]);
  const isRtl = useMemo(() => isHebrewText(content), [content]);
  const markdownComponents = useMemo(() => ({
    ...bidiComponents,
    table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
      <div className="my-4 overflow-x-auto">
        <table className="min-w-full border-collapse text-sm" {...props}>{children}</table>
      </div>
    ),
    thead: ({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <thead className="border-b border-border/60" {...props}>{children}</thead>
    ),
    th: ({ children, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
      <th className="px-3 py-2 text-left font-semibold text-foreground align-top" {...props}>{children}</th>
    ),
    td: ({ children, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
      <td className="px-3 py-2 align-top text-foreground/90" {...props}>{children}</td>
    ),
    tr: ({ children, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
      <tr className="border-b border-border/40 last:border-b-0" {...props}>{children}</tr>
    ),
  }), []);

  const hasDialogue = segments.some((s) => s.type === "dialogue" || s.type === "action" || s.type === "inner_voice" || s.type === "note");

  // If no dialogue detected, render as plain prose
  if (!hasDialogue && !locationChange) {
    return (
      <div {...storyElement("narrator-message")} dir={isRtl ? "rtl" : undefined} className={className}>
        {locationChange && <LocationCard location={locationChange} entityLookup={entityLookup} sceneImageUrl={sceneImageUrl} timeInfo={timeInfo} onLocationClick={onLocationClick} />}
        <div className={proseClasses} dir="auto">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
          {streaming && <span className="inline-block w-2 h-4 bg-primary/60 animate-pulse ml-0.5 align-text-bottom" />}
        </div>
        {diceRolls && diceRolls.length > 0 && <InlineDiceRolls rolls={diceRolls} />}
      </div>
    );
  }

  // Track which speakers have already shown their header (for grouping)
  let lastRenderedSpeaker: string | null = null;

  return (
    <div {...storyElement("narrator-message")} dir={isRtl ? "rtl" : undefined} className={`${className ?? ""} space-y-1.5`}>
      {/* Location transition card at top if present */}
      {locationChange && <LocationCard location={locationChange} entityLookup={entityLookup} sceneImageUrl={sceneImageUrl} timeInfo={timeInfo} onLocationClick={onLocationClick} />}

      {segments.map((seg, i) => {
        if (seg.type === "narration") {
          lastRenderedSpeaker = null;
          return (
            <div key={i} className={proseClasses} dir="auto">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{seg.text}</ReactMarkdown>
            </div>
          );
        }

        if (seg.type === "inner_voice") {
          lastRenderedSpeaker = null;
          return (
            <div
              key={i}
              className="relative rounded-lg border border-violet-500/30 bg-violet-500/5 px-3 py-2.5 my-1.5"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Brain className="w-3.5 h-3.5 text-violet-500" />
                <span className="text-[10px] font-semibold text-violet-500/80 uppercase tracking-widest">
                  Inner Voice
                </span>
              </div>
              <p className="text-xs italic text-foreground/80 leading-relaxed" dir="auto">
                {seg.text}
              </p>
            </div>
          );
        }

        if (seg.type === "note") {
          lastRenderedSpeaker = null;
          return (
            <div
              key={i}
              className="relative rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 my-1.5"
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <ScrollText className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[10px] font-semibold text-amber-500/80 uppercase tracking-widest">
                  {seg.noteTitle || "Notes"}
                </span>
              </div>
              <div dir="auto" className="text-xs text-foreground/90 leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:py-0.5">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{seg.text}</ReactMarkdown>
              </div>
            </div>
          );
        }

        if (seg.type === "action") {
          // Stage direction — centered italic
          return (
            <div key={i} className="text-center py-1">
              <span className="text-sm italic text-muted-foreground">
                — <em>{seg.text}</em> —
              </span>
            </div>
          );
        }

        // Dialogue segment
        const entity = seg.speaker
          ? entityLookup.get(seg.speaker.toLowerCase())
          : undefined;
        const initials = seg.speaker
          ? seg.speaker
              .split(/\s+/)
              .map((w) => w[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)
          : "?";
        const voiceKey = `npc:${seg.speaker}`;
        const isSpeaking = speakingKey === voiceKey;
        const isGrouped = lastRenderedSpeaker === seg.speaker;
        lastRenderedSpeaker = seg.speaker ?? null;

        // Entity mood from lookup
        const mood = entity?.emotional_state?.current_mood || seg.emotion;
        const emoji = moodEmoji(mood);

        // Speaker-specific border hue
        const hue = seg.speaker ? speakerHue(seg.speaker) : 0;

        return (
          <div
            key={i}
            className="flex items-start gap-3 pl-2 py-1 overflow-hidden min-w-0"
            style={{ borderLeft: `3px solid hsl(${hue}, 50%, 50%)` }}
          >
            {/* Avatar: show for first in group, invisible spacer for subsequent */}
            {!isGrouped ? (
              <Avatar className="h-9 w-9 shrink-0 border border-border shadow-sm mt-0.5">
                {entity?.image_url ? (
                  <AvatarImage src={entity.image_url} alt={seg.speaker} />
                ) : null}
                <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
            ) : (
              <div className="w-9 shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              {/* Speaker header: only for first in a group */}
              {!isGrouped && (
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span
                    className={`text-sm font-semibold text-foreground ${onSpeakerClick ? "cursor-pointer hover:text-primary hover:underline transition-colors" : ""}`}
                    onClick={onSpeakerClick && seg.speaker ? () => onSpeakerClick(seg.speaker!) : undefined}
                    role={onSpeakerClick ? "button" : undefined}
                  >
                    <bdi>{seg.speaker}</bdi>
                  </span>
                  {emoji && (
                    <span className="text-xs" title={mood}>
                      {emoji}
                    </span>
                  )}
                  {seg.emotion && !emoji && (
                    <span className="text-[10px] text-muted-foreground italic">
                      ({seg.emotion})
                    </span>
                  )}
                  {!streaming && (
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-muted-foreground hover:text-foreground"
                        onClick={() => onSpeakSegment(seg.text, voiceKey)}
                        disabled={isSpeaking}
                        title="Play voice"
                        aria-label="Play voice"
                      >
                        <Volume2 className="w-3 h-3" />
                      </Button>
                      {onDownloadSegment && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-muted-foreground hover:text-foreground"
                          onClick={() => onDownloadSegment(seg.text, voiceKey)}
                          title="Download audio"
                          aria-label="Download audio"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="bg-muted/50 border border-border/50 rounded-lg rounded-tl-none px-3 py-2 text-sm text-foreground leading-relaxed" dir="auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{seg.text}</ReactMarkdown>
              </div>
            </div>
          </div>
        );
      })}

      {/* Offscreen world activity */}
      {offscreenActions && offscreenActions.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors mt-2 w-full">
            <Globe className="w-3 h-3" />
            <span className="font-semibold uppercase tracking-widest">World Activity</span>
            <ChevronDown className="w-3 h-3 ml-auto" />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-1.5 space-y-1">
            {offscreenActions.map((oa, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground pl-2 border-l-2 border-muted">
                <span className="font-medium text-foreground/70">{oa.name}:</span>
                <span>{oa.action}{oa.motivation ? ` (${oa.motivation})` : ""}</span>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Inline dice rolls at bottom */}
      {diceRolls && diceRolls.length > 0 && <InlineDiceRolls rolls={diceRolls} />}

      {/* Streaming cursor */}
      {streaming && (
        <span className="inline-block w-2 h-4 bg-primary/60 animate-pulse ml-0.5 align-text-bottom" />
      )}
    </div>
  );
});

/** Compact inline dice roll badges */
function InlineDiceRolls({ rolls }: { rolls: DiceRoll[] }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2 max-w-full">
      {rolls.map((roll, i) => {
        // Heuristic: if reason contains "vs DC" extract for pass/fail
        const dcMatch = roll.reason?.match(/DC\s*(\d+)/i);
        const dc = dcMatch ? parseInt(dcMatch[1]) : null;
        const passed = dc !== null ? roll.result >= dc : null;

        return (
          <Badge
            key={i}
            variant="outline"
            className={`gap-1 text-[10px] sm:text-xs whitespace-normal break-words max-w-full ${
              passed === true
                ? "border-green-500/40 text-green-700 dark:text-green-400"
                : passed === false
                  ? "border-red-500/40 text-red-700 dark:text-red-400"
                  : "border-primary/30"
            }`}
          >
            <Dices className="w-3 h-3" />
            {roll.expression}: {roll.result}
            {dc !== null && (
              <span className="ml-0.5">
                vs DC {dc} {passed ? "✅" : "❌"}
              </span>
            )}
            {!dc && roll.reason && (
              <span className="ml-0.5 text-muted-foreground">— {roll.reason}</span>
            )}
          </Badge>
        );
      })}
    </div>
  );
}

/** Location transition card */
function LocationCard({
  location,
  entityLookup,
  sceneImageUrl,
  timeInfo,
  onLocationClick,
}: {
  location: string;
  entityLookup: Map<string, EntityMedia>;
  sceneImageUrl?: string;
  timeInfo?: { hour?: number; day?: number; phase?: string; weather?: string };
  onLocationClick?: (locationName: string) => void;
}) {
  const entity = entityLookup.get(location.toLowerCase());
  const imageUrl = sceneImageUrl || entity?.image_url;
  const clickable = !!onLocationClick;

  return (
    <div
      className={`relative rounded-lg overflow-hidden border border-border/60 bg-gradient-to-r from-accent/30 to-muted/30 mb-3 ${clickable ? "cursor-pointer hover:border-primary/40 transition-colors" : ""}`}
      onClick={clickable ? () => onLocationClick(location) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={location}
          className="w-full h-28 object-cover opacity-80"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      )}
      <div className={`flex items-center gap-2 px-3 py-2 ${imageUrl ? "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/90 to-transparent" : ""}`}>
        <MapPin className="w-4 h-4 text-primary shrink-0" />
        <span className="font-semibold text-sm text-foreground">{location}</span>
        {timeInfo?.phase && (
          <Badge variant="outline" className="text-[10px] gap-1 ml-auto">
            <Clock className="w-2.5 h-2.5" />
            {timeInfo.phase}{timeInfo.weather ? ` · ${timeInfo.weather}` : ""}
          </Badge>
        )}
      </div>
    </div>
  );
}
